import clsx from 'clsx'
import { AlertTriangle, Info, Zap } from 'lucide-react'

interface Alert {
  id: string
  alert_type: string
  urgency: number
  message: string
  action: string
  confidence: number
  status: string
  created_at: string
  mkg_citations: string[]
}

interface Props {
  alert: Alert
  onDismiss?: (id: string) => void
  onActedOn?: (id: string) => void
}

const urgencyColor = (u: number) => {
  if (u >= 4) return 'border-red-400 bg-red-50'
  if (u >= 3) return 'border-orange-400 bg-orange-50'
  return 'border-blue-300 bg-blue-50'
}

const urgencyIcon = (u: number) => {
  if (u >= 4) return <AlertTriangle size={18} className="text-red-500 shrink-0" />
  if (u >= 3) return <Zap size={18} className="text-orange-500 shrink-0" />
  return <Info size={18} className="text-blue-500 shrink-0" />
}

export default function AlertCard({ alert, onDismiss, onActedOn }: Props) {
  return (
    <div className={clsx('border-l-4 rounded-lg p-4', urgencyColor(alert.urgency))}>
      <div className="flex items-start gap-3">
        {urgencyIcon(alert.urgency)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {alert.alert_type}
            </span>
            <span className="text-xs text-gray-400">
              {Math.round(alert.confidence * 100)}% confidence
            </span>
          </div>
          <p className="text-sm text-gray-800">{alert.message}</p>
          {alert.action && (
            <p className="text-sm text-gray-600 mt-1 font-medium">→ {alert.action}</p>
          )}
          {alert.mkg_citations?.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              {alert.mkg_citations.join(' · ')}
            </p>
          )}
        </div>
      </div>
      {(onDismiss || onActedOn) && (
        <div className="flex gap-2 mt-3 ml-7">
          {onActedOn && (
            <button
              onClick={() => onActedOn(alert.id)}
              className="text-xs px-3 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
            >
              Acted on
            </button>
          )}
          {onDismiss && (
            <button
              onClick={() => onDismiss(alert.id)}
              className="text-xs px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  )
}
